import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { UserService } from '../../auth/services/user.service';
import { LocalService } from '../../core/services/local.service';
import { map } from 'rxjs/operators';
import { FilterValue } from '../../data-access/filters';
import { DealPipelineValue } from '../../deals/deal-pipelines/deal-pipeline-list-dropdown/deal-pipeline-list-dropdown.component';
import type { MenuState } from '../../layout/menu/menu.types';
import { FilterGroupValue } from '../../shared/filter-group/filter-group.models';
import { SortParamSchema } from '../../models/sort-param';
import { UnixDateRange } from '../../utils/date-range';
import { RangeDateType } from '../../data-access/ngb-date';

export interface Pagination {
  size: number,
}

export interface LocalSettings {
  pagination?: Partial<{
    users: Pagination,
    usersReport: Pagination,
    accounts: Pagination,
    allAccounts: Pagination,
    contacts: Pagination,
    documents: Pagination,
    accountDocuments: Pagination,
    userDocuments: Pagination,
    categorySettings: Pagination,
    tagSettings: Pagination,
    dailyRouteAccounts: Pagination,
    tasks: Pagination,
    allTasks: Pagination,
    notes: Pagination,
    dealNotes: Pagination,
    equipmentNotes: Pagination,
    equipmentPhotos: Pagination,
    sharedAccounts: Pagination,
    reportResults: Pagination,
    events: Pagination,
    userActivityLogs: Pagination,
    accountActivityLogs: Pagination,
    emailTemplates: Pagination,
    assignmentRules: Pagination,
    dealAssignmentRules: Pagination,
    deals: Pagination,
    accountDeals: Pagination,
    equipmentList: Pagination,
  }>,
  filter?: Partial<{
    allAccounts: FilterValue[] | null,
    locations: FilterValue[] | null,
    dashboardEvents: FilterValue[] | null,
    deals: FilterValue[] | null,
    accountDeals: FilterValue[] | null,
    assignmentRules: FilterValue[] | null,
  }>,
  dateRange?: Partial<{
    deals: {
      type: RangeDateType,
      dateRange: UnixDateRange,
    } | null,
  }>,
  accountCategoryChartUISettings?: Partial<{
    accounts: {
      opened?: boolean,
      collapsed?: boolean,
      position?: { x: number, y: number },
    },
    locations: {
      opened?: boolean,
      collapsed?: boolean,
      position?: { x: number, y: number },
    },
  }>,
  sort?: Partial<{
    accounts: SortParamSchema | undefined,
    dealsBoard: { [fieldName: string]: [ string, number ][] | undefined },
  }>,
  deals?: Partial<{
    viewType: 'board' | 'table',
    dealPipeline: DealPipelineValue,
  }>,
  menuState?: {
    view: MenuState['view'],
  },
  filterGroup?: Partial<{
    locations: {
      selectedMenu?: number,
      openedGroups?: string[],
      value?: FilterGroupValue | undefined,
    },
    accounts: {
      selectedMenu?: number,
      openedGroups?: string[],
      value?: FilterGroupValue | undefined,
    },
  }>,
}

@Injectable({
  providedIn: 'root',
})
export class GetLocalSettingsService {
  private userService = inject(UserService);
  private localStore = inject(LocalService);

  get(): Observable<LocalSettings | null> {
    const currentUserId = this.userService.currentUser?.uid;
    if (!currentUserId) throw new Error('Unexpected: Missed user ID');
    return of(this.localStore.get(`repmove-${ currentUserId }`)).pipe(
      map(data => data ? (JSON.parse(data) as LocalSettings) : null),
    );
  }
}
